import * as Sentry from '@sentry/react';
import { DocsAccessPage } from '_clean-code/pages/DocsAccess/DocsAccessPage';
import { readDocsRequest } from '_clean-code/pages/DocsAccess/docsAccessRequest';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  Navigate,
  RouteObject,
  createBrowserRouter,
  RouterProvider,
  useLocation
} from 'react-router-dom';
import CompleteSignup from 'pages/login-v2/complete-signup';
import LoginPageLayout from 'pages/login-v2/login-page-layout';
import MainLayoutRoute from './mainLayoutRoute';
import LoginV2 from 'pages/login-v2/login-page-v2';
import MicrosoftLoginPage from 'pages/login-v2/microsoft-login-page';
import ResetPasswordV2 from 'pages/login-v2/reset-password-v2';
import ForgotPasswordV2 from 'pages/login-v2/forgot-password-v2';
import { useAuth } from '_clean-code/api/hooks/useAuth';
import { getAmplifyToken } from '_clean-code/api/hooks/useAuthListener';
import { useUserCompanies } from '_clean-code/api/hooks/useUserCompanies';
import { useUserProfile } from '_clean-code/api/hooks/useUserProfile';
import { I18n } from '@utils/i18n';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { appVersion } from '@constants/config';
import { SELECTED_COMPANY_ID } from '@constants/index';
import jwtDecode from 'jwt-decode';
import { clearAppStorage } from '_clean-code/utils/storage';
import { identifyUser } from '_clean-code/analytics/identifyUser';

export const AuthWrapper: React.FC<{ element: React.ReactNode }> = ({
  element
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const prevIsAuth = useRef(isAuthenticated);
  const location = useLocation();

  // temporarily track forced log out events from La Cazadora
  if (prevIsAuth.current !== isAuthenticated) {
    prevIsAuth.current = isAuthenticated;
    const selectedCompanyId =
      localStorage.getItem(SELECTED_COMPANY_ID) &&
      !isNaN(Number(localStorage.getItem(SELECTED_COMPANY_ID)))
        ? Number(localStorage.getItem(SELECTED_COMPANY_ID))
        : undefined;
    const isLaCazadora = selectedCompanyId === 10085;
    if (!isAuthenticated && isLaCazadora) {
      const token = getAmplifyToken();
      const expiration = (() => {
        try {
          if (!token) return undefined;
          return jwtDecode(token) as { exp: number };
        } catch (e) {
          return undefined;
        }
      })();
      Sentry.captureException(
        new Error('AuthWrapper: user logged out from La Cazadora'),
        {
          extra: {
            isTokenSet: !!token,
            exp: expiration?.exp
          }
        }
      );
    }
  }

  if (isLoading) return null;

  if (!isAuthenticated) {
    // Not necessarily a logout: this also fires on the transient render where
    // the session is still being refreshed. Keep the analytics identity, or
    // that blip would re-anonymise the user in PostHog.
    clearAppStorage();
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (readDocsRequest()) return <Navigate to="/docs-access" replace />;

  return <>{element}</>;
};

export const NonAuthWrapper: React.FC<{ element: React.ReactNode }> = ({
  element
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const from = readDocsRequest()
    ? '/docs-access'
    : (location.state as { from?: string })?.from || '/realtime';
  if (isLoading) return null;
  return !isAuthenticated ? <>{element}</> : <Navigate to={from} replace />;
};

const createRoutes = (): RouteObject[] => [
  { path: '/docs-access', element: <DocsAccessPage /> },
  {
    path: '/login',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <LoginV2 />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/login/microsoft',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <MicrosoftLoginPage />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/forgot-password',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <ForgotPasswordV2 />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/reset-password',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <ResetPasswordV2 />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/invite/accept',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <CompleteSignup />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/*',
    element: <AuthWrapper element={<MainLayoutRoute />} />
  }
];

const router = createBrowserRouter(createRoutes());

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  // Wait for companies so SELECTED_COMPANY_ID is seeded in localStorage;
  // otherwise /identity/me is sent without x-company-id and may 403.
  const { data: companies } = useUserCompanies();
  const { userData } = useUserProfile({ enabled: !!companies });
  const { prodFull, prodBasic, prodV1, prodLegacy } = useFeatureFlags();

  const product = useMemo(
    () =>
      (prodFull && 'prodFull') ||
      (prodBasic && 'prodBasic') ||
      (prodV1 && 'prodV1') ||
      (prodLegacy && 'prodLegacy') ||
      '',
    [prodFull, prodBasic, prodV1, prodLegacy]
  );

  useEffect(() => {
    if (isAuthenticated && userData?.email) {
      if (userData?.email !== 'user@example.com') {
        // No-ops unless the identity or one of its properties actually changed,
        // including across page loads.
        identifyUser({
          email: userData.email,
          companyId: userData?.companyId,
          name: userData?.name,
          language: I18n._locale,
          role: userData?.role,
          appVersion,
          apiBaseUrl: import.meta.env.VITE_APP_ENDPOINT
        });
        // Identified users count toward the UserGuiding MAU quota; keep Pulsar staff out of it.
        if (!userData.email.toLowerCase().endsWith('@pulsarml.com')) {
          window.userGuiding?.identify(userData.email, {
            name: userData?.name,
            email: userData.email,
            language: I18n._locale,
            role: userData?.role,
            company: {
              id: userData?.companyId
            },
            product,
            companyName: userData?.companies?.[0]?.name
          });
        }
      }
    }
  }, [isAuthenticated, userData, product]);

  return <RouterProvider router={router} />;
};

export default AppRoutes;
